import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { AtlasDock, AtlasProvider, AtlasWelcome } from '../../features/atlas';
import { PostUpdateJourneyProvider } from '../../shared/hooks/usePostUpdateJourney';
import { isWelcomeTourCompleted } from '../../shared/onboarding/onboardingState';
import { OnboardingStateProvider } from '../../shared/onboarding/OnboardingStateProvider';
import { OwnerReviewBanner } from '../OwnerReviewBanner/OwnerReviewBanner';
import { SupportBanner } from '../support/SupportBanner';
import { TrialBanner } from '../TrialBanner/TrialBanner';
import { Navigation } from './Navigation';

const HIDE_ATLAS_PREFIXES = ['/auth', '/onboarding', '/solutions', '/pricing'] as const;

function shouldHideAtlas(pathname: string): boolean {
  if (pathname === '/') return true;
  return HIDE_ATLAS_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const Layout: React.FC = () => {
  const { user, organizations } = useAuth();
  const { isActive, loading: subscriptionLoading } = useSubscription();
  const { pathname } = useLocation();
  const isOnboardingRoute = pathname === '/onboarding' || pathname.startsWith('/onboarding/');
  const isMarketingShell =
    pathname === '/' ||
    pathname === '/solutions' ||
    pathname.startsWith('/solutions/') ||
    pathname === '/pricing' ||
    pathname.startsWith('/pricing/') ||
    pathname === '/help' ||
    pathname.startsWith('/help/');

  const atlasEligible =
    Boolean(user) &&
    organizations.length > 0 &&
    isActive &&
    !subscriptionLoading &&
    !shouldHideAtlas(pathname);

  const showAtlasWelcome = atlasEligible && !isWelcomeTourCompleted();

  return (
    <PostUpdateJourneyProvider>
      {/*
       * OnboardingStateProvider sits between PostUpdateJourney and AtlasProvider
       * so every Atlas mission (including post-update journey) can read the
       * server-backed snapshot via useOnboardingState(). See PR2 plan.
       */}
      <OnboardingStateProvider>
        <AtlasProvider>
          <div className={`app-layout-new${isMarketingShell ? ' app-layout-new--marketing' : ''}`}>
            <header className="app-header">
              <Navigation />
            </header>
            <SupportBanner />
            <TrialBanner />
            <OwnerReviewBanner />
            <main
              className={`app-main-content${isOnboardingRoute ? ' app-main-content--onboarding' : ''}`}
            >
              <Outlet />
            </main>
            <AtlasWelcome open={showAtlasWelcome} />
            {atlasEligible ? <AtlasDock /> : null}
          </div>
        </AtlasProvider>
      </OnboardingStateProvider>
    </PostUpdateJourneyProvider>
  );
};
